import { useContext } from 'react';
import {
  styled,
  Accordion,
  Button,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import MovingIcon from '@mui/icons-material/Moving';
import { Star } from '@mui/icons-material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ApartmentIcon from '@mui/icons-material/Apartment';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { ROUTES } from '../../../../../../constants';
import {
  getDemoAccountStatus,
  userHasCallPlanAccess,
} from '../../../../../../request/config';
import { AppContext } from '../../../../appContext';
import { FieldContext } from '../../fieldContext';
import useFeatureToggles from '../../../../../../hooks/useFeatureToggles';
import { trackMenuNavigation } from '../../../../../../trackers/mixpanel';
import { useIsAuthorized } from '../../../../../../hooks/useIsAuthorized';

const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: '0px',
}));

const StyledViewListButton = styled(Button)(
  ({ theme: { themeColors }, isActive }) => ({
    width: '100%',
    padding: '12px',
    minHeight: '24px',
    justifyContent: 'flex-start',
    color: isActive
      ? themeColors.buttonActiveContentColor
      : themeColors.buttonContentColor,
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '24px',
    backgroundColor: isActive
      ? themeColors.buttonSecondaryBackgroundColour
      : 'none',
    '&:hover': {
      backgroundColor: isActive
        ? themeColors.buttonSecondaryBackgroundColour
        : themeColors.buttonHoverBackgroundColor,
      color: themeColors.buttonHoverContentColor,
    },
    '& .MuiSvgIcon-root': {
      color: themeColors.listIconColor,
    },
  })
);

const StyledAccordion = styled(Accordion)(() => ({
  backgroundColor: 'inherit',
  backgroundImage: 'none',
  boxShadow: 'none',
  '&:before': {
    background: 'none',
  },
  marginTop: '12px !important',
}));

const StyledAccordionSummary = styled(AccordionSummary)(
  ({ theme: { themeColors } }) => ({
    color: themeColors.secondaryDefaultIconPrimaryColor,
    padding: 0,
  })
);

const StyledAccordionHeader = styled(Typography)((props) => ({
  color: props.theme.themeColors.buttonGroupHeaderColor,
  padding: '0px 12px',
}));

const StyledAccordionDetails = styled(AccordionDetails)(() => ({
  paddingLeft: 0,
  paddingRight: 0,
}));

const StyledButtonContent = styled('div')(() => ({
  display: 'flex',
  width: '100%',
}));

const StyledButtonCount = styled('div')(() => ({
  marginLeft: 'auto',
}));

const StyledSVG = styled('svg')`
  height: 20px;
  width: 20px;
  & path {
    fill: ${(props) => props.theme.themeColors.listIconColor};
  }
`;

const VacationIcon = () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M2 7.95005C2.88208 3.91185 6.56168 2.94409 8.82092 2.71327C11.063 0.402593 15.0045 4.238 16.835 6.65848C15.7946 6.93723 14.843 7.02271 13.9817 6.96939C14.5121 7.96516 14.8558 9.17725 14.8558 10.6351C13.4017 10.3376 11.4856 9.41813 10.0746 8.22625C10.146 11.4627 9.09626 16.0569 8.49993 18.144H3.99993C6.64919 16.0835 8.08536 11.2502 8.9206 8.43928C9.03513 8.05381 9.13837 7.70637 9.23189 7.40933C9.09254 7.25167 8.96382 7.09109 8.84749 6.92824C7.56389 8.76102 4.9271 10.5324 3.49993 11.144C3.49993 9.79521 3.97372 8.54114 4.65289 7.4585C3.76402 7.64708 2.91118 7.79354 2.2105 7.91387L2 7.95005Z"
    fill="#5E708D"
  />
);

const StethoscopeIcon = () => (
  <>
    <mask
      id="mask0_470_1725"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="21"
      height="21"
    >
      <rect x="0.5" y="0.359863" width="20" height="20" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_470_1725)">
      <path
        d="M11.6042 18.3599C10.2484 18.3599 9.09349 17.8808 8.13942 16.9227C7.18536 15.9646 6.70833 14.8047 6.70833 13.4432V12.8182C5.52778 12.6654 4.53125 12.1481 3.71875 11.2661C2.90625 10.3842 2.5 9.33209 2.5 8.10986V3.35986H5V2.35986H6.5V5.85986H5V4.85986H4V8.11945C4 9.01584 4.31597 9.78 4.94792 10.4119C5.57986 11.0439 6.34722 11.3599 7.25 11.3599C8.15278 11.3599 8.92014 11.0439 9.55208 10.4119C10.184 9.78 10.5 9.01584 10.5 8.11945V4.85986H9.5V5.85986H8V2.35986H9.5V3.35986H12V8.10986C12 9.28139 11.6424 10.3 10.9271 11.1656C10.2118 12.0312 9.30556 12.5682 8.20833 12.7765V13.464C8.20833 14.4131 8.5386 15.2198 9.19915 15.8842C9.85969 16.5485 10.6618 16.8807 11.6054 16.8807C12.549 16.8807 13.3507 16.5485 14.0104 15.8842C14.6701 15.2198 15 14.4131 15 13.464V12.2272C14.4861 12.0795 14.066 11.7904 13.7396 11.3599C13.4132 10.9293 13.25 10.4293 13.25 9.85986C13.25 9.16542 13.4931 8.57514 13.9792 8.08903C14.4653 7.60292 15.0556 7.35986 15.75 7.35986C16.4444 7.35986 17.0347 7.60292 17.5208 8.08903C18.0069 8.57514 18.25 9.16542 18.25 9.85986C18.25 10.4293 18.0868 10.9293 17.7604 11.3599C17.434 11.7904 17.0139 12.0821 16.5 12.2349V13.4432C16.5 14.8047 16.023 15.9646 15.0689 16.9227C14.1148 17.8808 12.9599 18.3599 11.6042 18.3599ZM15.75 10.8599C16.0333 10.8599 16.2708 10.764 16.4625 10.5724C16.6542 10.3807 16.75 10.1432 16.75 9.85986C16.75 9.57653 16.6542 9.33903 16.4625 9.14736C16.2708 8.9557 16.0333 8.85986 15.75 8.85986C15.4667 8.85986 15.2292 8.9557 15.0375 9.14736C14.8458 9.33903 14.75 9.57653 14.75 9.85986C14.75 10.1432 14.8458 10.3807 15.0375 10.5724C15.2292 10.764 15.4667 10.8599 15.75 10.8599Z"
        fill="#5E708D"
      />
    </g>
  </>
);

const ENTITY_OVERVIEW_ROUTES = {
  outlet: `${ROUTES.ENTITY_OVERVIEW}${ROUTES.OUTLET}`,
  direct_account: `${ROUTES.ENTITY_OVERVIEW}${ROUTES.DIRECT_ACCOUNT}`,
};

const ENTITY_OVERVIEW_ICONS = {
  outlet: <ApartmentIcon />,
  direct_account: <WorkOutlineOutlinedIcon />,
};

export const ViewsList = ({ project }) => {
  const history = useHistory();
  const location = useLocation();
  const featureToggles = useFeatureToggles();
  const { profile } = useContext(AppContext);
  const {
    maptualListMetadata,
    maptualList,
    entityViews,
    regionHasCuratedList,
  } = useContext(FieldContext);
  const isAuthorized = useIsAuthorized();

  const isDemoAccount = getDemoAccountStatus();
  const routeContains = (route) => location.pathname.includes(route);
  const handleViewListButtonClick = (view) => {
    if (routeContains(view)) {
      return;
    }

    trackMenuNavigation(view);
    const route = `${ROUTES.FIELD}${ROUTES.PROJECTS}/${project.metadata.projectId}/maptualListId/${maptualListMetadata.maptualListId}${view}`;
    history.push(route);
  };

  const handleHcpListSelect = () => {
    const defaultMapView =
      featureToggles(profile.userGroup, 'prioritizeMapView') &&
      featureToggles(profile.userGroup, 'mapView') &&
      isAuthorized(['view-map']);
    if (defaultMapView) {
      return handleViewListButtonClick(`${ROUTES.HCPS}${ROUTES.MAP}`);
    }
    return handleViewListButtonClick(ROUTES.HCPS);
  };

  return (
    <Container>
      {(isDemoAccount ||
        (userHasCallPlanAccess(profile, regionHasCuratedList) &&
          featureToggles(profile.userGroup, 'curatedList'))) && (
        <StyledViewListButton
          isActive={routeContains(ROUTES.CALL_PLAN)}
          startIcon={<TrackChangesIcon />}
          onClick={() => handleViewListButtonClick(ROUTES.CALL_PLAN)}
          className={routeContains(ROUTES.CALL_PLAN) ? 'Mui-selected' : ''} // for automated tests
        >
          Call Plan
        </StyledViewListButton>
      )}
      <StyledViewListButton
        isActive={routeContains(ROUTES.HCPS)}
        startIcon={
          <StyledSVG>
            <StethoscopeIcon />
          </StyledSVG>
        }
        onClick={() => handleHcpListSelect()}
        className={routeContains(ROUTES.HCPS) ? 'Mui-selected' : ''} // for automated tests
      >
        HCPs
      </StyledViewListButton>

      {featureToggles(profile.userGroup, 'showAccountView') &&
        entityViews.map((view) => {
          const entityViewRoute = ENTITY_OVERVIEW_ROUTES[view.entityType];

          return (
            <StyledViewListButton
              key={view.entityType}
              isActive={routeContains(entityViewRoute)}
              startIcon={ENTITY_OVERVIEW_ICONS[view.entityType]}
              onClick={() => handleViewListButtonClick(entityViewRoute)}
              className={routeContains(entityViewRoute) ? 'Mui-selected' : ''} // for automated tests
            >
              {view.displayName}
            </StyledViewListButton>
          );
        })}

      {featureToggles(profile.userGroup, 'showTerritoryOverview') && (
        <StyledViewListButton
          isActive={routeContains(ROUTES.TERRITORY_OVERVIEW)}
          startIcon={<MovingIcon />}
          onClick={() => handleViewListButtonClick(ROUTES.TERRITORY_OVERVIEW)}
          className={
            routeContains(ROUTES.TERRITORY_OVERVIEW) ? 'Mui-selected' : ''
          } // for automated tests
        >
          Territory Overview
        </StyledViewListButton>
      )}
      <StyledAccordion disableGutters="true" defaultExpanded="true">
        <StyledAccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <StyledAccordionHeader>HCP Custom Lists</StyledAccordionHeader>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <StyledViewListButton
            isActive={routeContains(`${ROUTES.CUSTOM}${ROUTES.STARRED}`)}
            startIcon={<Star />}
            onClick={() =>
              handleViewListButtonClick(`${ROUTES.CUSTOM}${ROUTES.STARRED}`)
            }
            className={
              routeContains(`${ROUTES.CUSTOM}${ROUTES.STARRED}`)
                ? 'Mui-selected'
                : ''
            } // for automated tests
          >
            <StyledButtonContent>
              <div>Starred</div>
              <StyledButtonCount>
                {maptualList?.userCreatedListCounts?.starred || ''}
              </StyledButtonCount>
            </StyledButtonContent>
          </StyledViewListButton>
          <StyledViewListButton
            isActive={routeContains(`${ROUTES.CUSTOM}${ROUTES.NO_SEE}`)}
            startIcon={<VisibilityOffIcon />}
            onClick={() =>
              handleViewListButtonClick(`${ROUTES.CUSTOM}${ROUTES.NO_SEE}`)
            }
            className={
              routeContains(`${ROUTES.CUSTOM}${ROUTES.NO_SEE}`)
                ? 'Mui-selected'
                : ''
            } // for automated tests
          >
            <StyledButtonContent>
              <div>No See</div>
              <StyledButtonCount>
                {maptualList?.userCreatedListCounts?.noSee || ''}
              </StyledButtonCount>
            </StyledButtonContent>
          </StyledViewListButton>
          <StyledViewListButton
            isActive={routeContains(
              `${ROUTES.CUSTOM}${ROUTES.LONG_TERM_LEAVE}`
            )}
            startIcon={
              <StyledSVG>
                <VacationIcon />
              </StyledSVG>
            }
            onClick={() =>
              handleViewListButtonClick(
                `${ROUTES.CUSTOM}${ROUTES.LONG_TERM_LEAVE}`
              )
            }
            className={
              routeContains(`${ROUTES.CUSTOM}${ROUTES.LONG_TERM_LEAVE}`)
                ? 'Mui-selected'
                : ''
            } // for automated tests
          >
            <StyledButtonContent>
              <div>Long term leave</div>
              <StyledButtonCount>
                {maptualList?.userCreatedListCounts?.longTermLeave || ''}
              </StyledButtonCount>
            </StyledButtonContent>
          </StyledViewListButton>
        </StyledAccordionDetails>
      </StyledAccordion>
    </Container>
  );
};
